import { castArray, isEqual, isNil, omit } from 'lodash-es';

import { SEARCH_KEY, SEARCH_TARGET_KEY } from '../constants/search.constants';
import { type FilterValue } from '../filter-controls-panel/interfaces/filters/base.filter';
import { type ActiveFilters } from '../store/interfaces';

/**
 * True when the only thing that moved is the field the search points at, while the
 * search box is empty — the query is still "everything", so there is nothing to refetch.
 */
export function isInertSearchTargetChange(params: {
  previous: ActiveFilters | null | undefined;
  next: ActiveFilters;
}): boolean {
  const { previous, next } = params;

  if (!previous || next.search) {
    return false;
  }

  return (
    previous[SEARCH_TARGET_KEY] !== next[SEARCH_TARGET_KEY] &&
    isEqual(omit(previous, SEARCH_TARGET_KEY), omit(next, SEARCH_TARGET_KEY))
  );
}

/**
 * Removes all null, undefined and empty array values from the filters.
 */
export function cleanFilters<T>(filters: Required<T>): Required<T> {
  const cleaned = {} as Required<T>;

  for (const key in filters) {
    if (!Object.prototype.hasOwnProperty.call(filters, key)) {
      continue;
    }

    const value = filters[key];

    if (isNil(value)) {
      continue;
    }

    if (Array.isArray(value) && value.length === 0) {
      continue;
    }

    cleaned[key] = value;
  }

  return cleaned;
}

export function isActiveArrayFilter<T>(
  value: FilterValue | T[] | null | undefined,
): value is T[] {
  return Array.isArray(value) && value.length > 0;
}

/**
 * True as soon as one filter carries a value. An empty list then says it is the
 * filters that emptied it, rather than the plain "no data".
 */
export function hasActiveFilterValue(
  activeFilters: ActiveFilters | null | undefined,
): boolean {
  if (!activeFilters) {
    return false;
  }

  return Object.values(activeFilters).some(value => {
    // A consultation date always carries a value: counting it would pin every
    // empty list to "filtering".
    if (value instanceof Date) {
      return false;
    }

    // `0` narrows the list like any other option value (a ground floor); an off
    // switch does not.
    return castArray(value).some(
      item => !isEmptyFilterValue(item) && item !== false,
    );
  });
}

/**
 * An empty filter carries no value at all. `0` and `false` are values: only nil and
 * the empty string say the user picked nothing.
 */
export function isEmptyFilterValue(value: unknown): boolean {
  return isNil(value) || value === '';
}

/**
 * The filter changes worth reporting: a key the user touched whose value moved.
 *
 * Dirtiness alone would report the whole set the user has ever touched on every
 * emission, and the value diff alone would report the view's defaults filling
 * in. Free-text search is reported on its empty/non-empty flip only — the panel
 * emits on a 100 ms debounce, so its value would otherwise count a change per
 * word typed.
 */
export function reportableFilterChanges(params: {
  previous: ActiveFilters | null | undefined;
  next: ActiveFilters;
  dirtyKeys: readonly string[];
}): { filterKey: string; isActive: boolean }[] {
  const { previous, next, dirtyKeys } = params;

  return dirtyKeys
    .map(key => ({
      filterKey: key,
      isActive: hasActiveFilterValue({ [key]: next[key] }),
      wasActive: hasActiveFilterValue({ [key]: previous?.[key] }),
      hasMoved: !isEqual(previous?.[key], next[key]),
    }))
    .filter(({ filterKey, isActive, wasActive, hasMoved }) =>
      filterKey === SEARCH_KEY ? isActive !== wasActive : hasMoved,
    )
    .map(({ filterKey, isActive }) => ({ filterKey, isActive }));
}
